/**
 * Account scope — the single source for "which account's data may this
 * query/write touch" (RS-2, docs/plan/implementation/rules-tenant-scoping.md).
 *
 * Firestore evaluates a collection query against its constraints, not the
 * returned docs, so an admin/manager query must constrain
 * `corporateAccountId` (or a field the rules derive the account from, such
 * as `propertyId`) to be provable. Super admin has a global rule and gets no
 * constraint.
 *
 * Creates use `resolvePropertyAccountId()`: a business object belongs to its
 * property's account, never to whoever created it (super admin owns nothing —
 * `firestore-patterns.md` Ownership Model). The rules enforce this on create.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { db, auth } from '../firebase'

export interface AccountScope {
  uid: string
  role: string | null
  /** The requester's own account; null for super admin. */
  corporateAccountId: string | null
  isSuperAdmin: boolean
}

/** A real account id: not empty, not the legacy `'default'` sentinel. */
export function isRealAccountId(id: unknown): id is string {
  return typeof id === 'string' && id !== '' && id !== 'default'
}

/** Read the signed-in user's role + account from their user doc. */
export async function getAccountScope(): Promise<AccountScope> {
  const uid = auth.currentUser?.uid
  if (!uid) throw new Error('Not signed in')
  const snap = await getDoc(doc(db, 'users', uid))
  const data = snap.exists() ? snap.data() : {}
  const role = (data.role as string | undefined) ?? null
  const caid = data.corporateAccountId
  return {
    uid,
    role,
    corporateAccountId: isRealAccountId(caid) ? caid : null,
    isSuperAdmin: role === 'super_admin',
  }
}

/**
 * Constraints that make a staff query provable under the account-scoped
 * rules: `[]` for super admin, otherwise `corporateAccountId == mine`.
 * Throws for a non-super-admin with no account (they can read nothing).
 */
export function accountConstraints(scope: AccountScope): QueryConstraint[] {
  if (scope.isSuperAdmin) return []
  if (!scope.corporateAccountId) {
    throw new Error(
      'No account on this user; account-scoped data is unavailable'
    )
  }
  return [where('corporateAccountId', '==', scope.corporateAccountId)]
}

/** Convenience: read the scope and return its constraints. */
export async function currentAccountConstraints(): Promise<QueryConstraint[]> {
  return accountConstraints(await getAccountScope())
}

/**
 * The account that owns a property — the account every doc about that
 * property (assignments, alerts, scans, photos, history) must carry.
 * Returns null when the property is missing or has no real account.
 */
export async function resolvePropertyAccountId(
  propertyId: string | null | undefined
): Promise<string | null> {
  if (!propertyId) return null
  try {
    const snap = await getDoc(doc(db, 'properties', propertyId))
    const caid = snap.exists() ? snap.data().corporateAccountId : null
    return isRealAccountId(caid) ? caid : null
  } catch {
    return null
  }
}

/**
 * Which property-keyed records the current user may change. The RS-2 admin
 * update rules on propertyAssignments, tenantPropertyAssignments,
 * maintenanceAlerts and propertyAssignmentHistory all require the record's
 * property to be in the requester's account, so a record whose property was
 * deleted (or belongs to another account) can't be changed by an admin, and
 * one such write fails a whole batch (Eric's tenant delete, 2026-10-14).
 * Returns null for the super admin (unscoped: every record is writable).
 */
export async function getWritablePropertyIds(): Promise<Set<string> | null> {
  const scope = await currentAccountConstraints()
  if (scope.length === 0) return null
  const snap = await getDocs(query(collection(db, 'properties'), ...scope))
  return new Set(snap.docs.map(d => d.id))
}
